import { unwrapResult } from '@reduxjs/toolkit';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useSelectedLanguageContext } from '~/components/SelectedLanguage';
import { authStoreStateSelector } from '~/selectors';
import { AppDispatch } from '~/store';
import { redirectToApplicationConsole, redirectToOrigin } from '~/utils/helper';

import { fetchAuthenticatedUserDetails } from '../../slice';

import { authenticatedUserIsAdmin } from './authenticatedUserIsAdmin';

export interface UsePrivateRouteOutput {
  isAuthenticated: boolean;
  isLoading: boolean;
  failedToLoad: boolean;
  hasData: boolean;
  isUserPasswordChangeRequired: boolean;
}

export const usePrivateRoute = (): UsePrivateRouteOutput => {
  const dispatch: AppDispatch = useDispatch();

  const { userDetails, userLoginData, isLoading, hasData, failedToLoad } =
    useSelector(authStoreStateSelector);
  const isAuthenticated = !!userDetails;

  const { setLanguage } = useSelectedLanguageContext();

  useEffect(() => {
    if (!isAuthenticated) {
      dispatch(fetchAuthenticatedUserDetails())
        .then(unwrapResult)
        .then(({ userDetails: { language } }) => {
          setLanguage(language.replace(/["']/g, ''));
        })
        .catch(() => {
          redirectToOrigin('Not authenticated');
        });
    }
  }, [dispatch, isAuthenticated, userDetails, setLanguage]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    if (authenticatedUserIsAdmin(userDetails)) {
      redirectToApplicationConsole('Private Route detected Super User account');
      return;
    }
  }, [isAuthenticated, userDetails]);

  return {
    isAuthenticated,
    isLoading,
    hasData,
    failedToLoad,
    isUserPasswordChangeRequired:
      userLoginData?.fLoginNeedsPasswordChange ?? false,
  };
};
